import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import {
  FETCH_STOCK_CATEGORY_LATEST_STOCKS,
  SHOPIFY_UPDATE_INVENTORY,
} from "./graphql";

const CreateShopifyAdjustmentView = ({ shipment, onCompleted }) => {
  const { loading, error, data } = useQuery(
    FETCH_STOCK_CATEGORY_LATEST_STOCKS,
    {
      variables: { stockCategoryId: shipment.toLocationId },
      fetchPolicy: "network-only",
    },
  );
  const [submitShopifyInventoryUpdate, submitRes] = useMutation(
    SHOPIFY_UPDATE_INVENTORY,
    {
      variables: {
        instance: "AB",
        referenceDocumentUri: "wis://stock-shipment/" + shipment.id,
        items: shipment.lines.map((line) => ({
          sku: line.product.number,
          delta: line.qty,
        })),
      },
      onCompleted() {
        Alert("success", "Shopify inventory updated.");
        onCompleted();
      },
      onError(error) {
        window.alert(parseError(error));
      },
    },
  );

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const stockCategoryLatestStocks = JSON.parse(
    data.stockCategoryLatestStocks || "{}",
  );
  const shopifyStocks = JSON.parse(data.shopifyStocks || "[]");

  const productStocks = Object.values(stockCategoryLatestStocks).reduce(
    (stocks, { product_id, latest_qty }) => ({
      ...stocks,
      [product_id]: latest_qty,
    }),
    {},
  );

  const products = shipment.lines.map((line) => {
    const productStock = productStocks[line.product.id];
    const shopifyStock = shopifyStocks.find(
      (s) => s.item.sku === line.product.number,
    );

    const shopifyStockOnHand = shopifyStock
      ? shopifyStock.quantities.find((i) => i.name === "on_hand")
      : null;
    const shopifyStockAvailable = shopifyStock
      ? shopifyStock.quantities.find((i) => i.name === "available")
      : null;

    return {
      ...line.product,
      stock: productStock || 0,
      shopifyStockOnHand: shopifyStockOnHand ? shopifyStockOnHand.quantity : 0,
      shopifyStockAvailable: shopifyStockAvailable
        ? shopifyStockAvailable.quantity
        : 0,
      delta: line.qty,
    };
  });

  return (
    <div className="text-sm">
      <div>
        <p>
          You can create a Shopify inventory adjustment for the products in this
          shipment. The adjustment will be based on the quantity of each product
          in the shipment, and will be applied to the Shopify inventory for each
          product.
        </p>
        <p>
          Note that wis is <b>not setting</b> the Shopify inventory to the
          quantity in the shipment, but rather <b>adjusting</b> the Shopify
          inventory by the quantity in the shipment. For example, if a product
          has 10 units in Shopify and the shipment has 5 units, the adjustment
          will be +5 units, resulting in 15 units in Shopify.
        </p>
      </div>

      <div className="-mx-2 mt-4">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">WIS Stock</th>
              <th className="text-right">Changes</th>
              <th className="text-right">Shopify Available</th>
              <th className="text-right">Shopify On Hand</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className="border-y">
                <td>
                  [{product.number}] {product.name}
                </td>
                <td className="text-right">{product.stock}</td>
                <td className="text-right">
                  {product.shopifyStockAvailable > 0 ? "+" : null}
                  {product.delta}
                </td>
                <td className="text-right">
                  {product.shopifyStockAvailable} →{" "}
                  {product.shopifyStockAvailable + product.delta}
                </td>
                <td className="text-right">
                  {product.shopifyStockOnHand} →{" "}
                  {product.shopifyStockOnHand + product.delta}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <Button
          primary
          onClick={submitShopifyInventoryUpdate}
          loading={submitRes.loading}
        >
          Submit Adjustment to Shopify
        </Button>
      </div>
    </div>
  );
};

export default CreateShopifyAdjustmentView;
